<template>
  <div>
    <div class="d-flex justify-space-between align-center mb-2 flex-wrap gap-2">
      <h6 class="ma-0">Lançamentos</h6>
      <div class="d-flex gap-2">
        <v-btn
          @click="runBackfill"
          color="secondary"
          depressed
          small
          :loading="backfilling"
        >
          Backfill Faturas
        </v-btn>
        <v-btn @click="openCreate" color="primary" depressed small>
          Novo Lançamento
        </v-btn>
      </div>
    </div>

    <v-alert v-if="error" type="error" class="mb-3">
      {{ error }}
    </v-alert>

    <div class="d-flex gap-2 align-center flex-wrap mb-2">
      <v-chip class="pa-0">
        <div>
          <v-btn icon @click="previousReference">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          {{ reference }}
          <v-btn icon @click="nextReference" :disabled="hasNextMonth">
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </div>
      </v-chip>

      <v-select
        v-model="filterType"
        :items="typeFilterItems"
        label="Tipo"
        dense
        outlined
        hide-details
        clearable
        style="max-width: 140px"
        @change="loadData"
      />
      <v-select
        v-model="filterStatus"
        :items="statusFilterItems"
        label="Status"
        dense
        outlined
        hide-details
        clearable
        style="max-width: 160px"
        @change="loadData"
      />
      <v-select
        v-model="filterCategory"
        :items="categoryFilterItems"
        label="Categoria"
        dense
        outlined
        hide-details
        clearable
        style="max-width: 180px"
      />

      <v-text-field
        v-model="search"
        append-icon="mdi-magnify"
        label="Pesquisar"
        single-line
        dense
        hide-details
        outlined
      />
      <v-btn @click="loadData" icon small>
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </div>

    <v-row dense class="mb-3">
      <v-col cols="6" sm="4" md>
        <v-card outlined class="pa-2 text-center">
          <div class="text-caption text--secondary">Recebido</div>
          <div class="font-weight-medium success--text">
            {{ summary.received | currency }}
          </div>
        </v-card>
      </v-col>
      <v-col cols="6" sm="4" md>
        <v-card outlined class="pa-2 text-center">
          <div class="text-caption text--secondary">Pendente</div>
          <div class="font-weight-medium warning--text">
            {{ summary.pending | currency }}
          </div>
        </v-card>
      </v-col>
      <v-col cols="6" sm="4" md>
        <v-card outlined class="pa-2 text-center">
          <div class="text-caption text--secondary">Saídas</div>
          <div class="font-weight-medium error--text">
            {{ summary.expenses | currency }}
          </div>
        </v-card>
      </v-col>
      <v-col cols="6" sm="4" md>
        <v-card outlined class="pa-2 text-center">
          <div class="text-caption text--secondary">Saldo</div>
          <div
            class="font-weight-medium"
            :class="summary.balance >= 0 ? 'success--text' : 'error--text'"
          >
            {{ summary.balance | currency }}
          </div>
        </v-card>
      </v-col>
    </v-row>

    <v-data-table
      v-model="selected"
      :loading="loading"
      :items="visibleEntries"
      :headers="headers"
      :search="search"
      item-key="id"
      show-select
      dense
      :items-per-page="-1"
      hide-default-footer
      class="elevation-0 cash-entries-table"
      :class="{ 'mb-16': selected.length }"
    >
      <template v-slot:item.description="{ item }">
        <div class="d-flex align-center cash-entry-main">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                small
                class="mr-2 flex-shrink-0"
                :color="typeMeta(item).color"
                v-bind="attrs"
                v-on="on"
              >
                {{ typeMeta(item).icon }}
              </v-icon>
            </template>
            <span>{{ typeMeta(item).label }}</span>
          </v-tooltip>
          <div class="cash-entry-description">
            <span>{{ item.description }}</span>
            <v-chip
              v-if="item.invoiceId"
              x-small
              label
              class="ml-1"
              color="primary"
              outlined
            >
              Fatura
            </v-chip>
          </div>
        </div>
      </template>
      <template v-slot:item.Category.name="{ item }">
        <span v-if="item.Category">{{ item.Category.name }}</span>
        <span v-else class="text--disabled">Sem categoria</span>
      </template>
      <template v-slot:item.Company.name="{ item }">
        <template v-if="item.Company">
          <div class="d-flex align-center">
            <base-avatar
              :seed="item.Company.id"
              :size="20"
              avatarStyle="glass"
              color="secondary lighten-2"
              class="flex-shrink-0"
            />
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <span
                  class="ml-2 cash-company-name"
                  v-bind="attrs"
                  v-on="on"
                >
                  {{ item.Company.name }}
                </span>
              </template>
              <span>{{ item.Company.name }}</span>
            </v-tooltip>
          </div>
        </template>
        <span v-else class="text--secondary">—</span>
      </template>
      <template v-slot:item.expectedAmount="{ item }">
        <div class="cash-amount">
          <div
            class="font-weight-medium"
            :class="
              item.status === 'CANCELLED'
                ? 'text--disabled text-decoration-line-through'
                : amountColor(item)
            "
          >
            {{ displayAmount(item) | currency }}
          </div>
          <div
            v-if="showExpectedHint(item)"
            class="text-caption text--secondary lh-1"
          >
            previsto {{ item.expectedAmount | currency }}
          </div>
        </div>
      </template>
      <template v-slot:item.dueDate="{ item }">
        <span class="text-no-wrap">{{ item.dueDate | date }}</span>
      </template>
      <template v-slot:item.status="{ item }">
        <v-chip :color="getStatusColor(item)" label small>
          {{ getStatusLabel(item) }}
        </v-chip>
      </template>
      <template v-slot:item.actions="{ item }">
        <div class="d-flex justify-end flex-nowrap">
          <v-tooltip v-if="item.status === 'PENDING'" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                small
                class="mr-1"
                color="success"
                v-bind="attrs"
                v-on="on"
                @click="settleEntry(item)"
              >
                mdi-check
              </v-icon>
            </template>
            <span>{{
              item.type === "EXPENSE"
                ? "Registrar pagamento"
                : "Registrar recebimento"
            }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                small
                class="mr-1"
                v-bind="attrs"
                v-on="on"
                @click="editEntry(item)"
              >
                mdi-pencil
              </v-icon>
            </template>
            <span>Editar</span>
          </v-tooltip>
          <v-tooltip v-if="item.status === 'PENDING'" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                small
                color="error"
                v-bind="attrs"
                v-on="on"
                @click="cancelEntry(item)"
              >
                mdi-close
              </v-icon>
            </template>
            <span>Cancelar</span>
          </v-tooltip>
        </div>
      </template>
    </v-data-table>

    <v-slide-y-reverse-transition>
      <div v-if="selected.length" class="cash-selection-bar">
        <v-card class="rounded-lg px-4 py-2" elevation="8">
          <div class="d-flex align-center flex-wrap cash-selection-content">
            <div class="cash-selection-count">
              <div class="font-weight-medium lh-1">
                {{ selected.length }}
                {{ selected.length > 1 ? "lançamentos" : "lançamento" }}
                selecionado{{ selected.length > 1 ? "s" : "" }}
              </div>
              <v-btn text x-small class="px-0" @click="selected = []">
                Limpar
              </v-btn>
              <div
                v-if="cancelledSelected"
                class="text-caption text--disabled lh-1"
              >
                Cancelados não entram nos valores
              </div>
            </div>
            <div class="text-end">
              <small class="d-block text--secondary">Recebido</small>
              <b class="success--text">{{ reducedSelected.received | currency }}</b>
            </div>
            <div class="text-end">
              <small class="d-block text--secondary">A receber</small>
              <b class="warning--text">{{ reducedSelected.pending | currency }}</b>
            </div>
            <div class="text-end">
              <small class="d-block text--secondary">Pago</small>
              <b class="error--text">{{ reducedSelected.expenses | currency }}</b>
            </div>
            <div class="text-end">
              <small class="d-block text--secondary">A pagar</small>
              <b class="error--text">{{
                reducedSelected.expensePending | currency
              }}</b>
            </div>
            <div class="text-end">
              <small class="d-block text--secondary">Saldo</small>
              <b :class="selectedBalance >= 0 ? 'success--text' : 'error--text'">
                {{ selectedBalance | currency }}
              </b>
              <small class="d-block text--disabled">recebido − pago</small>
            </div>
          </div>
        </v-card>
      </div>
    </v-slide-y-reverse-transition>

    <CashEntryForm @created="loadData" />
    <SettleEntryModal @settled="loadData" />
    <CashEntryEditModal @updated="loadData" />
  </div>
</template>

<script>
import moment from "moment";
import CASH from "@/services/ultimate/cash.js";
import CASH_DEFINITIONS from "@/definitions/CashDefinitions";
import CashEntryForm from "@/components/ultimate/cash/CashEntryForm.vue";
import SettleEntryModal from "@/components/ultimate/cash/SettleEntryModal.vue";
import CashEntryEditModal from "@/components/ultimate/cash/CashEntryEditModal.vue";

export default {
  name: "CashEntries",
  components: {
    CashEntryForm,
    SettleEntryModal,
    CashEntryEditModal,
  },
  data() {
    return {
      error: null,
      loading: false,
      backfilling: false,
      entries: [],
      summary: {
        billed: 0,
        received: 0,
        pending: 0,
        expenses: 0,
        balance: 0,
      },
      search: "",
      reference: moment().format("MM/YYYY"),
      filterType: null,
      filterStatus: null,
      filterCategory: null,
      selected: [],
      CASH_DEFINITIONS,
      headers: [
        { text: "Lançamento", value: "description" },
        { text: "Categoria", value: "Category.name" },
        { text: "Empresa", value: "Company.name" },
        { text: "Valor", value: "expectedAmount", align: "end" },
        { text: "Vencimento", value: "dueDate" },
        { text: "Status", value: "status" },
        { text: "Ações", value: "actions", sortable: false, align: "end" },
      ],
      typeFilterItems: [
        { text: "Entrada", value: "INCOME" },
        { text: "Saída", value: "EXPENSE" },
      ],
      statusFilterItems: [
        { text: "Pendente", value: "PENDING" },
        { text: "Recebido/Pago", value: "SETTLED" },
        { text: "Cancelado", value: "CANCELLED" },
      ],
    };
  },
  computed: {
    hasNextMonth() {
      return moment(this.reference, "MM/YYYY").isSame(
        moment().startOf("month"),
        "month"
      );
    },
    categoryFilterItems() {
      const byId = new Map();
      for (const entry of this.entries) {
        if (entry.Category) byId.set(entry.Category.id, entry.Category.name);
      }
      const items = Array.from(byId, ([value, text]) => ({ text, value }));
      items.sort((a, b) => a.text.localeCompare(b.text, "pt-BR"));
      return [{ text: "Sem categoria", value: "__EMPTY__" }, ...items];
    },
    visibleEntries() {
      if (!this.filterCategory) return this.entries;
      if (this.filterCategory === "__EMPTY__") {
        return this.entries.filter((entry) => !entry.Category);
      }
      return this.entries.filter(
        (entry) => entry.Category && entry.Category.id === this.filterCategory
      );
    },
    reducedSelected() {
      return this.selected.reduce(
        (acc, item) => {
          if (item.status === "CANCELLED") return acc;
          const expected = Number(item.expectedAmount) || 0;
          const settled = Number(item.settledAmount) || 0;
          if (item.type === "INCOME") {
            if (item.status === "SETTLED") acc.received += settled;
            if (item.status === "PENDING") acc.pending += expected;
          }
          if (item.type === "EXPENSE") {
            if (item.status === "SETTLED") acc.expenses += settled;
            if (item.status === "PENDING") acc.expensePending += expected;
          }
          return acc;
        },
        { received: 0, pending: 0, expenses: 0, expensePending: 0 }
      );
    },
    selectedBalance() {
      return this.reducedSelected.received - this.reducedSelected.expenses;
    },
    cancelledSelected() {
      return this.selected.some((item) => item.status === "CANCELLED");
    },
  },
  methods: {
    getQueryParams() {
      return {
        reference: this.reference,
        type: this.filterType || undefined,
        status: this.filterStatus || undefined,
      };
    },
    async loadData() {
      try {
        this.loading = true;
        this.error = null;
        this.selected = [];
        const params = this.getQueryParams();
        const [entriesRes, summaryRes] = await Promise.all([
          CASH.getAll(params),
          CASH.getSummary({ reference: this.reference }),
        ]);
        this.entries = entriesRes.entries;
        this.summary = summaryRes.summary;
      } catch (error) {
        this.error = error.message || "Erro ao buscar lançamentos";
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    previousReference() {
      this.reference = moment(this.reference, "MM/YYYY")
        .subtract(1, "month")
        .format("MM/YYYY");
      this.loadData();
    },
    nextReference() {
      this.reference = moment(this.reference, "MM/YYYY")
        .add(1, "month")
        .format("MM/YYYY");
      this.loadData();
    },
    openCreate() {
      this.$emit("cash-entry-create");
    },
    settleEntry(entry) {
      this.$emit("cash-entry-settle", entry);
    },
    editEntry(entry) {
      this.$emit("cash-entry-edit", entry);
    },
    async cancelEntry(entry) {
      if (!confirm("Deseja cancelar este lançamento?")) return;
      try {
        await CASH.cancel(entry.id);
        await this.loadData();
      } catch (error) {
        alert(error.message || "Erro ao cancelar lançamento");
      }
    },
    async runBackfill() {
      if (
        !confirm(
          "Criar lançamentos pendentes para faturas fechadas sem lançamento?"
        )
      )
        return;
      try {
        this.backfilling = true;
        const result = await CASH.backfill();
        alert(
          `Backfill concluído: ${result.created} criados, ${result.skipped} ignorados`
        );
        await this.loadData();
      } catch (error) {
        alert(error.message || "Erro ao executar backfill");
      } finally {
        this.backfilling = false;
      }
    },
    typeMeta(item) {
      return (
        CASH_DEFINITIONS.type[item.type] || {
          label: item.type,
          color: "grey",
          icon: "mdi-help-circle-outline",
        }
      );
    },
    displayAmount(item) {
      if (item.status === "SETTLED") return Number(item.settledAmount) || 0;
      return Number(item.expectedAmount) || 0;
    },
    amountColor(item) {
      return item.type === "EXPENSE" ? "error--text" : "success--text";
    },
    showExpectedHint(item) {
      if (item.status !== "SETTLED") return false;
      return this.getDifference(item) !== 0;
    },
    getDifference(item) {
      return (item.settledAmount || 0) - (item.expectedAmount || 0);
    },
    getStatusLabel(item) {
      if (item.status === "SETTLED") {
        return CASH_DEFINITIONS.settledStatusLabel[item.type] || "Recebido";
      }
      return CASH_DEFINITIONS.status[item.status]?.label || item.status;
    },
    getStatusColor(item) {
      if (item.status === "SETTLED") {
        return item.type === "EXPENSE" ? "error" : "success";
      }
      return CASH_DEFINITIONS.status[item.status]?.color || "grey";
    },
    highlightEntryFromQuery() {
      const entryId = this.$route.query.entryId;
      if (!entryId || this.entries.length === 0) return;
      const entry = this.entries.find((e) => e.id === entryId);
      if (entry && entry.status === "PENDING") {
        this.settleEntry(entry);
      }
    },
  },
  async mounted() {
    await this.loadData();
    this.highlightEntryFromQuery();
  },
};
</script>

<style scoped>
.cash-entries-table >>> tbody td {
  height: auto !important;
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}

.cash-entry-description {
  white-space: normal;
  line-height: 1.3;
}

.cash-company-name {
  display: inline-block;
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

.cash-amount {
  white-space: nowrap;
}

.cash-selection-bar {
  position: fixed;
  z-index: 7;
  left: 16px;
  right: 88px;
  bottom: 20px;
  display: flex;
  justify-content: center;
  pointer-events: none;
}

.cash-selection-bar > * {
  pointer-events: auto;
  max-width: 100%;
}

.cash-selection-content {
  gap: 20px;
}

.cash-selection-count {
  min-width: 140px;
}

@media (max-width: 600px) {
  .cash-selection-bar {
    left: 8px;
    right: 72px;
    bottom: 16px;
  }
}
</style>
